import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, skip } from 'rxjs/operators';
import {
  ClinicalTask,
  ClinicalTaskListRequest,
  ClinicalTaskListResponse,
  ClinicalTaskRequest,
  SearchClinicalTasksParams,
} from 'src/app/models/clients/clinical-task.model';
import { AcuityLevel } from 'src/app/models/acuity.model';
import { ClinicalTaskPriority, PatientTaskGroup } from 'src/app/models/clients/patient-task-group.model';
import { Patient } from 'src/app/models/patient.model';
import { UserTag } from 'src/app/models/user.model';
import { AlertFlagsService } from 'src/app/services/alert-flags.service';
import { NewAuthService } from 'src/app/services/auth/new-auth-service.service';
import { InitialDataModel } from 'src/app/services/models/initial-data-model.service';
import { PatientListNavigationService } from 'src/app/services/patient-list-navigation.service';
import { SnackService } from 'src/app/services/snack.service';
import { TaskHistoryNavigationService } from 'src/app/services/task-history-navigation.service';
import { UtilsService } from 'src/app/services/utils.service';
import { DataService } from '../../../services/data.service';
import { PatientService } from '../../../services/patient.service';
import { PatientNamePipe } from '../../pipes/patient-name.pipe';
import { ClientAbbreviationsConfigService } from '../../services/client-abbreviations-config.service';
import { TaskService } from './services/task.service';

@Component({
  selector: 'app-task-history',
  templateUrl: './task-history.component.html',
  styleUrls: ['./task-history.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class TaskHistoryComponent implements OnInit, OnDestroy {
  private static readonly PRIORITY_RANK: Record<ClinicalTaskPriority, number> = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
  };
  private static readonly PRIORITY_BY_RANK: ClinicalTaskPriority[] = ['low', 'medium', 'high', 'critical'];
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  // When true (default), task-history renders the Task detail/notes panels itself.
  // When false, the parent (e.g., careplan-home) hosts those panels and listens to the
  // taskDetailOpened / taskNotesOpened / taskPanelClosed outputs.
  @Input() embedDetailNotes = true;
  @Output() taskDetailOpened = new EventEmitter<ClinicalTask>();
  @Output() taskNotesOpened = new EventEmitter<ClinicalTask>();
  @Output() taskPanelClosed = new EventEmitter<void>();

  displayedColumns = [
    'patient_name_lower',
    'task_name_lower',
    'priority_score',
    'category',
    'assignee_id',
    'createdBy',
    'due_date',
    'chart',
    'close',
  ];
  taskList = new MatTableDataSource<ClinicalTask>();
  showMyAssignedTasks = true;
  currentUser: Patient;
  loading = false;
  assignedView = true;
  allDatesView = false;
  tasksObject = {};
  allTasks: ClinicalTask[] = [];
  filteredTasks = [];
  currentTask: any;
  currentTaskId: string;
  showDetail = false;
  showNotes = false;
  datesOptions: { startDate: Date | null; endDate: Date | null; label: string }[] = [];
  filterForm: UntypedFormGroup;
  subscription: Subscription = new Subscription();
  // Tracks the in-flight Tier 3 alert lookup so a newer request (e.g. fast pagination) can cancel
  // the previous one before it resolves and writes onto the wrong page's rows.
  private releaseAlertSub?: Subscription;
  tasksByState: { label: string; value: boolean | null }[] = [];
  searchParams: any = {
    assigned: this.assignedView,
    completed: false,
    sortByField: 'priority_score',
    sortDirection: 'desc',
  };
  activePatient: Patient;
  currentPage = 1;
  pageSize = 10;
  totalRecords = 0;
  clientsAccount: string[];
  providers: Patient[];
  currentSortColumn = 'priority_score'; // Initialize to match matSortActive in template
  pinnedTasks: ClinicalTask[] = [];
  groupedTaskList = new MatTableDataSource<PatientTaskGroup>();
  // Track expanded groups by patient_id so the expansion survives the buildPatientGroups
  // rebuild that follows getClinicalTasks (object references would otherwise be stale).
  // A Set allows several groups to stay open at once, which the default-expanded preference needs.
  expandedGroupPatientIds = new Set<string>();
  // Drives the "Expand All" toggle in the grouped view. Initialized once from the user's profile
  // preference, then acts as a sticky session override (kept open across refreshes while on).
  expandAll = false;
  groupDisplayedColumns: string[] = [];
  taskDisplayedColumns: string[] = [];
  expandedDetailColumns: string[] = ['expandedDetail'];
  clientAbbreviations: Record<string, string> = {};
  // Seed the initial expand-all state from the user's profile preference only once per component
  // load (i.e. once per entry to the view); after that the in-list toggle owns the state.
  private defaultExpansionApplied = false;
  // Held across closeTask → getClinicalTasks so we can keep the "Closing task…" snackbar
  // up until the refresh completes, then swap it for the outcome message.
  private pendingCloseSnack: { ref: MatSnackBarRef<any>; outcome: 'success' | 'error' } | null = null;

  constructor(
    public db: AngularFirestore,
    private authService: NewAuthService,
    public data: DataService,
    private patientService: PatientService,
    private router: Router,
    private fb: UntypedFormBuilder,
    private utilsService: UtilsService,
    private taskService: TaskService,
    private snackService: SnackService,
    public dataModel: InitialDataModel,
    private taskHistoryNavService: TaskHistoryNavigationService,
    private patientListNavService: PatientListNavigationService,
    private patientNamePipe: PatientNamePipe,
    private clientAbbreviationsConfig: ClientAbbreviationsConfigService,
    private alertFlagsService: AlertFlagsService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.clientAbbreviationsConfig.getAbbreviations().subscribe((abbreviations) => {
        this.clientAbbreviations = abbreviations;
      })
    );
    this.dataModel.getClientAccounts().then((clients) => {
      this.clientsAccount = clients.map((client) => client.client_account_id);
      this.pageSize = this.activePatient ? 10 : 100;
      // Initialize search params with default sort
      this.searchParams = {
        ...this.searchParams,
      };
      this.getClinicalTasks(this.searchParams);
    });
    this.tasksByState = [
      { label: 'All', value: null },
      { label: 'Completed', value: true },
      { label: 'Open Tasks', value: false },
    ];
    this.datesOptions = [
      { startDate: null, endDate: null, label: 'All' },
      {
        startDate: this.utilsService.getTodayDate(),
        endDate: this.utilsService.getTodayEODDate(),
        label: 'Today tasks',
      },
      {
        startDate: this.utilsService.getThisWeekDate(this.patientService?.currentPatientService?.timezone).thisWeekDate,
        endDate: this.utilsService.getThisWeekDate(this.patientService?.currentPatientService?.timezone)
          .thisWeekEndDate,
        label: 'This week tasks',
      },
      {
        startDate: this.utilsService.getThisMonthDate(this.patientService?.currentPatientService?.timezone)
          .thisMonthDate,
        endDate: this.utilsService.getThisMonthDate(this.patientService?.currentPatientService?.timezone)
          .thisMonthEndDate,
        label: 'This month tasks',
      },
      {
        startDate: this.utilsService.getLastWeekDate(this.patientService?.currentPatientService?.timezone).lastWeekDate,
        endDate: this.utilsService.getLastWeekDate(this.patientService?.currentPatientService?.timezone)
          .lastWeekEndDate,
        label: 'Last week tasks',
      },
      {
        startDate: this.utilsService.getLastMonthDate(this.patientService?.currentPatientService?.timezone)
          .lastMonthDate,
        endDate: this.utilsService.getLastMonthDate(this.patientService?.currentPatientService?.timezone)
          .lastMonthEndDate,
        label: 'Last month tasks',
      },
    ];
    this.currentUser = this.authService.user;
    this.providers = (Object.values(this.dataModel.allClientUsers) as Patient[])
      .filter((client) => client.roles.isClient)
      .sort((clientA, clientB) => this.utilsService.sortUsersByAlphabeticOrder(clientA, clientB));
    this.filterForm = this.fb.group({
      dateFilter: [''],
      taskByState: [this.tasksByState[2]],
      taskOwner: [this.providers.find((provider) => provider.user_id === this.currentUser?.user_id)],
    });
    this.activePatient = this.patientService.currentPatientService;
    if (this.activePatient) {
      this.showMyAssignedTasks = false;
      this.watchActivePatientChanges();
    }
    if (!this.activePatient) {
      this.restoreNavigationState();
    }
    this.setFilterChanges();
    this.updateDisplayedColumns();
  }

  updateDisplayedColumns(): void {
    if (this.activePatient) {
      this.displayedColumns = [
        'task_name_lower',
        'priority_score',
        'category',
        'assignee_id',
        'createdBy',
        'due_date',
        'close',
      ];
      this.groupDisplayedColumns = [];
      this.taskDisplayedColumns = [];
      return;
    }

    const baseColumns = [
      'client',
      'patient_name_lower',
      'task_name_lower',
      'priority_score',
      'category',
      'assignee_id',
      'createdBy',
      'due_date',
      'chart',
      'close',
    ];
    this.groupDisplayedColumns = this.showMyAssignedTasks ? ['pin', ...baseColumns] : baseColumns;
    const baseTaskColumns = [
      'task_name_lower',
      'priority_score',
      'category',
      'assignee_id',
      'createdBy',
      'due_date',
      'close',
    ];
    this.taskDisplayedColumns = this.showMyAssignedTasks ? ['pin', ...baseTaskColumns] : baseTaskColumns;
  }

  restoreNavigationState(): void {
    const savedNav = this.taskHistoryNavService.currentTaskHistoryNavObs$;
    this.subscription.add(
      savedNav.subscribe((nav) => {
        if (nav) {
          if (nav.taskOwner) {
            const matchedProvider = this.providers.find((p) => p.user_id === nav.taskOwner.user_id);
            if (matchedProvider) {
              this.filterForm.get('taskOwner').setValue(matchedProvider);
            }
          }
          if (nav.taskByState) {
            const matchedState = this.tasksByState.find((s) => s.value === nav.taskByState.value);
            if (matchedState) {
              this.filterForm.get('taskByState').setValue(matchedState);
            }
          }
          if (nav.dateFilter) {
            const matchedDate = this.datesOptions.find((d) => d.label === nav.dateFilter.label);
            if (matchedDate) {
              this.filterForm.get('dateFilter').setValue(matchedDate);
            }
          }
          if (nav.searchParams) {
            this.searchParams = nav.searchParams;
          }
          if (nav.currentPage) {
            this.currentPage = nav.currentPage;
          }
          if (nav.showMyAssignedTasks !== undefined) {
            this.showMyAssignedTasks = nav.showMyAssignedTasks;
          }
          if (nav.assignedView !== undefined) {
            this.assignedView = nav.assignedView;
          }
          this.updateDisplayedColumns();
        }
      })
    );
  }

  onToggleChange(event: MatSlideToggleChange): void {
    this.showMyAssignedTasks = event.checked;
    this.currentPage = 1;
    this.updateDisplayedColumns();
    this.getClinicalTasks(this.searchParams);
  }

  setFilterChanges() {
    this.filterForm.get('dateFilter').valueChanges.subscribe((dateSelected) => {
      this.searchParams = { ...this.searchParams, startDate: dateSelected?.startDate, endDate: dateSelected?.endDate };
      if (dateSelected) {
        this.getClinicalTasks(this.searchParams);
      } else {
        this.getClinicalTasks();
      }
    });
    this.filterForm.get('taskByState').valueChanges.subscribe((taskState) => {
      // Open Tasks: sort by priority_score desc; All/Completed: sort by due_date asc
      const isOpenTasks = taskState.value === false;
      const sortByField = isOpenTasks ? 'priority_score' : 'due_date';
      const sortDirection = isOpenTasks ? 'desc' : 'asc';
      this.currentSortColumn = sortByField;
      this.searchParams = {
        ...this.searchParams,
        completed: taskState.value,
        sortByField,
        sortDirection,
      };
      if (taskState) {
        this.getClinicalTasks(this.searchParams);
      } else {
        this.getClinicalTasks();
      }
    });
    this.filterForm.get('taskOwner').valueChanges.subscribe((taskOwner) => {
      this.currentPage = 1;
      if (taskOwner) {
        if (taskOwner.user_id === this.currentUser?.user_id) {
          this.showMyAssignedTasks = true;
        } else {
          this.showMyAssignedTasks = false;
        }
        this.updateDisplayedColumns();
        this.getClinicalTasks(this.searchParams);
      } else {
        this.getClinicalTasks();
      }
    });
  }

  getClinicalTasks(
    searchParams: {
      completed?: boolean;
      assigned?: boolean;
      startDate?: Date;
      endDate?: Date;
      sortByField?: string;
      sortDirection?: string;
    } = {}
  ) {
    this.loading = true;
    const { startDate, endDate, completed, sortByField, sortDirection } = searchParams;
    const groupByPatient = !this.activePatient;
    const searchParamsTask = {
      ...(completed !== null && { isCompleted: completed }),
      startDate,
      endDate,
      sortByField: sortByField || 'priority_score',
      sortDirection: sortDirection || 'desc',
    } as SearchClinicalTasksParams;
    if (this.patientService.currentPatientService?.user_id) {
      searchParamsTask.patientId = this.patientService.currentPatientService.user_id;
    }
    const searchParamsEndpoint: ClinicalTaskListRequest = {
      page: this.currentPage,
      pageSize: this.pageSize,
      providerId: this.currentUser?.user_id,
      searchParams: searchParamsTask,
      ...(groupByPatient && { groupByPatient: true }),
    };

    const handleError = (): void => {
      this.loading = false;
    };

    if (this.showMyAssignedTasks === true) {
      this.taskService.getTasksByProvider(searchParamsEndpoint).subscribe({
        next: (response: ClinicalTaskListResponse) => this.getTasks(response),
        error: handleError,
      });
    } else {
      const taskOwner = this.filterForm.get('taskOwner').value?.user_id;
      const search: ClinicalTaskRequest = {
        page: this.currentPage,
        pageSize: this.pageSize,
        startDate,
        endDate,
        ...(completed !== null && { isCompleted: completed }),
        ...(!this.activePatient && { client_accounts: this.clientsAccount }),
        ...(!this.activePatient &&
          taskOwner && {
            assignee_id: taskOwner,
          }),
        ...(sortByField &&
          sortDirection && {
            sortByField,
            sortDirection,
          }),
        ...(this.authService.user?.roles?.isAdmin && { isAdmin: true }),
        ...(groupByPatient && { groupByPatient: true }),
      };

      if (this.activePatient) {
        this.taskService.getTasksByPatient(this.patientService.currentPatientService.user_id, search).subscribe({
          next: (response: ClinicalTaskListResponse) => this.getTasks(response),
          error: handleError,
        });
      } else {
        this.taskService.getTasksByClient(search).subscribe({
          next: (response: ClinicalTaskListResponse) => this.getTasks(response),
          error: handleError,
        });
      }
    }
  }

  getTasks(response: ClinicalTaskListResponse): void {
    const mapTask = (task: ClinicalTask) => ({
      ...task,
      due_date: new Date(task.due_date),
      create_date: task.create_date && !isNaN(new Date(task.create_date).getTime()) ? new Date(task.create_date) : null,
      patientName: this.patientNamePipe.transform(
        task['patient']?.firstName ?? '',
        task['patient']?.lastName ?? 'N/A',
        false
      ),
    });

    const records = response.records.map(mapTask);

    if (this.activePatient) {
      this.pinnedTasks = [];
      this.taskList.data = records;
      this.groupedTaskList.data = [];
    } else {
      this.taskList.data = [];
      this.pinnedTasks = [];
      this.groupedTaskList.data = this.buildPatientGroups(records);
      this.syncGroupExpansion();
    }

    this.applyReleaseAlerts();

    this.totalRecords = response.totalRecords;
    this.loading = false;

    if (this.pendingCloseSnack) {
      this.pendingCloseSnack.ref.dismiss();
      const message = this.pendingCloseSnack.outcome === 'success' ? 'Task completed' : 'Error completing task';
      const panel = this.pendingCloseSnack.outcome === 'success' ? 'success-snackbar' : 'error-snackbar';
      this.snackService.genericSnackBar(message, [panel], 5000);
      this.pendingCloseSnack = null;
    }
  }

  toggleGroup(group: PatientTaskGroup): void {
    if (group.taskCount === 1) {
      return;
    }
    if (this.expandedGroupPatientIds.has(group.patient_id)) {
      this.expandedGroupPatientIds.delete(group.patient_id);
      // Collapsing any group means we are no longer showing everything expanded, so the
      // "Expand All" toggle flips off and refreshes stop re-expanding (manual choice wins).
      this.expandAll = false;
    } else {
      this.expandedGroupPatientIds.add(group.patient_id);
    }
  }

  toggleExpandAll(checked: boolean): void {
    this.expandAll = checked;
    if (checked) {
      this.expandAllGroups();
    } else {
      this.expandedGroupPatientIds.clear();
    }
  }

  changePage(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    if (this.sort && this.sort.active && this.sort.direction) {
      this.searchParams = {
        ...this.searchParams,
        sortByField: this.sort.active,
        sortDirection: this.sort.direction,
      };
    }
    this.getClinicalTasks(this.searchParams);
  }

  async viewPatient(id: string): Promise<void> {
    const patientIds = this.activePatient
      ? this.taskList.data.map((task) => task.patient_id)
      : this.groupedTaskList.data.map((group) => group.patient_id);
    const uniquePatientIds = [...new Set(patientIds)];
    const currentIndex = uniquePatientIds.indexOf(id);
    if (currentIndex >= 0 && uniquePatientIds.length > 1) {
      this.patientListNavService.setChartNavigationContext({ patientIds: uniquePatientIds, currentIndex });
    } else {
      this.patientListNavService.clearChartNavigationContext();
    }
    this.router.navigate(['clinical', 'patient-detail', id, 'home']);
  }

  closeTask(clinicalTask: ClinicalTask): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    const snackRef = this.snackService.genericSnackBar('Closing task…', ['info-snackbar'], 0);
    this.taskService.markTaskAsCompleted(clinicalTask).subscribe({
      next: () => {
        this.pendingCloseSnack = { ref: snackRef, outcome: 'success' };
        this.getClinicalTasks(this.searchParams);
      },
      error: () => {
        snackRef.dismiss();
        this.snackService.genericSnackBar('Error completing task', ['error-snackbar'], 5000);
        this.loading = false;
      },
    });
  }

  toggleAsignedOwned() {
    this.assignedView = !this.assignedView;
    this.searchParams = { ...this.searchParams, assigned: this.assignedView };
    this.getClinicalTasks(this.searchParams);
  }

  filterPastDue() {
    const now = new Date();
    this.allDatesView = !this.allDatesView;

    if (this.assignedView && this.allDatesView) {
      this.filteredTasks = this.allTasks.filter(
        (p) => p.assignee_id === this.currentUser?.user_id && p.due_date.toDate() <= now
      );
    } else if (this.assignedView && !this.allDatesView) {
      this.filteredTasks = this.allTasks.filter((p) => p.assignee_id === this.currentUser?.user_id);
    } else if (!this.assignedView && this.allDatesView) {
      this.filteredTasks = this.allTasks.filter((p) => p.creator_id === this.currentUser?.user_id);
    } else {
      this.filteredTasks = this.allTasks.filter(
        (p) => p.creator_id === this.currentUser?.user_id && p.due_date.toDate() <= now
      );
    }
  }

  editTask(task): void {
    this.currentTask = task;
    this.toggleDetail();
  }

  // Always close the panel; only refresh the task list when the panel
  // reports a data change (save / acuity resolve emit true, cancel emits false).
  clearCurrentTask(refresh: boolean): void {
    this.currentTask = null;
    this.showDetail = false;
    this.showNotes = false;
    if (refresh) {
      this.getClinicalTasks(this.searchParams);
    }
    if (!this.embedDetailNotes) {
      this.taskPanelClosed.emit();
    }
  }

  openNotes(event): void {
    this.currentTask = event;
    if (this.embedDetailNotes) {
      this.showNotes = true;
      this.showDetail = false;
    } else {
      this.taskNotesOpened.emit(event);
    }
  }

  toggleDetail(isNew?: boolean) {
    if (isNew) {
      this.currentTask = this.patientService.currentPatientService
        ? {
            patient_id: this.patientService.currentPatientService.user_id,
          }
        : null;
    }
    if (this.embedDetailNotes) {
      this.showDetail = true;
      this.showNotes = false;
    } else {
      this.taskDetailOpened.emit(this.currentTask);
    }
  }

  refreshTasks(): void {
    this.getClinicalTasks(this.searchParams);
  }

  handleSort(event: Sort): void {
    this.currentPage = 1;
    let sortField = event.active;
    // When sorting by priority column, use priority_score for Open Tasks, priority for All/Completed
    if (event.active === 'priority_score') {
      const isOpenTasks = this.filterForm.get('taskByState').value?.value === false;
      sortField = isOpenTasks ? 'priority_score' : 'priority';
    }
    this.currentSortColumn = sortField;
    this.searchParams.sortByField = sortField;
    this.searchParams.sortDirection = event.direction;
    this.searchParams.page = 1;
    if (event.active && event.direction) {
      this.getClinicalTasks(this.searchParams);
    }
  }

  getPriorityChipColor(priority: string): string {
    const key = priority?.trim().toLowerCase();
    if (key === 'critical') {
      return 'warn';
    } else if (key === 'high') {
      return 'accent';
    } else if (key === 'medium') {
      return 'primary';
    }
    return 'default';
  }

  getAcuityChipColor(acuity: AcuityLevel | null | undefined): string {
    const key = acuity?.trim().toLowerCase();
    if (key === 'high') {
      return 'warn';
    } else if (key === 'medium') {
      return 'accent';
    } else if (key === 'low') {
      return 'primary';
    }
    return 'default';
  }

  pinTask(task: ClinicalTask, event: Event): void {
    event.stopPropagation();

    if (
      !task.pinned ||
      (task.pinned && task.pinnedProperties == null) ||
      (task.pinned && task.pinnedProperties.pinnedByUserId === this.authService.user.user_id)
    ) {
      task.pinned = !task.pinned;

      if (task.pinnedProperties == null) {
        task.pinnedProperties = {
          pinnedByUserId: null,
          pinnedByFullName: null,
        };
      }

      task.pinnedProperties.pinnedByUserId = task.pinned ? this.authService.user.user_id : null;
      task.pinnedProperties.pinnedByFullName = task.pinned
        ? this.authService.user.firstName + ' ' + this.authService.user.lastName
        : null;

      this.taskService.pinTask(task, this.authService.user.user_id).subscribe({
        next: () => {
          this.getClinicalTasks(this.searchParams);
        },
        error: () => {
          task.pinned = !task.pinned;
          this.snackService.genericSnackBar('Error updating pin state', ['error-snackbar'], 5000);
        },
      });
    } else {
      this.snackService.genericSnackBar(
        `Only ${task.pinnedProperties.pinnedByFullName} can unpin this task.`,
        ['error-snackbar'],
        10000
      );
    }
  }

  isPinnedSeparatorRow(index: number): boolean {
    if (this.pinnedTasks.length === 0) {
      return false;
    }
    if (index !== this.pinnedTasks.length - 1) {
      return false;
    }
    return this.taskList.data.length > this.pinnedTasks.length;
  }

  isTaskPinnable(task: ClinicalTask): boolean {
    return !task.completed;
  }

  ngOnDestroy(): void {
    this.releaseAlertSub?.unsubscribe();
    this.subscription.unsubscribe();
    if (!this.activePatient) {
      this.taskHistoryNavService.setCurrentTaskHistoryNav({
        searchParams: this.searchParams,
        currentPage: this.currentPage,
        taskOwner: this.filterForm.get('taskOwner').value,
        taskByState: this.filterForm.get('taskByState').value,
        dateFilter: this.filterForm.get('dateFilter').value,
        showMyAssignedTasks: this.showMyAssignedTasks,
        assignedView: this.assignedView,
      });
    }
  }

  // Runs after every group rebuild. On the first load it seeds the expand-all state from the
  // user's profile preference; afterwards, while expand-all is on it keeps every multi-task group
  // open (sticky across refreshes, including groups that first appear in a later refresh).
  private syncGroupExpansion(): void {
    if (!this.defaultExpansionApplied) {
      this.defaultExpansionApplied = true;
      this.expandAll = this.authService.user?.config?.defaultTasksExpanded ?? false;
    }
    // Drop ids for patients no longer in the current result set (e.g. after paginating) so the
    // Set stays bounded and same-page manual toggles still survive pin / close-task refreshes.
    this.pruneExpandedToCurrentGroups();
    if (this.expandAll) {
      this.expandAllGroups();
    }
  }

  private pruneExpandedToCurrentGroups(): void {
    const currentIds = new Set(this.groupedTaskList.data.map((group) => group.patient_id));
    this.expandedGroupPatientIds.forEach((id) => {
      if (!currentIds.has(id)) {
        this.expandedGroupPatientIds.delete(id);
      }
    });
  }

  // Expand every multi-task group. Single-task groups are never expandable, so they are excluded.
  private expandAllGroups(): void {
    this.groupedTaskList.data.forEach((group) => {
      if (group.taskCount > 1) {
        this.expandedGroupPatientIds.add(group.patient_id);
      }
    });
  }

  private watchActivePatientChanges(): void {
    this.subscription.add(
      this.patientService.currentPatient$
        .pipe(
          skip(1),
          filter((patient) => !!patient?.user_id),
          distinctUntilChanged((prev, curr) => prev?.user_id === curr?.user_id)
        )
        .subscribe((patient) => {
          this.activePatient = patient;
          this.currentPage = 1;
          this.getClinicalTasks(this.searchParams);
        })
    );
  }

  // Flags patients that have an open Tier 3 vital alert so the grouped (no active patient) view can
  // show a release_alert icon. Only the patients currently on screen are queried. The fetch is async,
  // so the icon appears a moment after the table renders. Not used in the single-patient view.
  private applyReleaseAlerts(): void {
    if (this.activePatient) {
      return;
    }
    const uniqueIds = [...new Set(this.groupedTaskList.data.map((group) => group.patient_id).filter(Boolean))];
    if (!uniqueIds.length) {
      return;
    }
    // Cancel any in-flight lookup so a stale page response can't overwrite the current page's icons.
    this.releaseAlertSub?.unsubscribe();
    this.releaseAlertSub = this.alertFlagsService.getOpenTier3PatientIds(uniqueIds).subscribe({
      next: (alertIds) => {
        const alertIdSet = new Set(alertIds);
        this.groupedTaskList.data.forEach((group) => {
          group.hasReleaseAlert = alertIdSet.has(group.patient_id);
        });
        // Reassign so the mat-table picks up the mutated rows and re-renders the icon column.
        this.groupedTaskList.data = [...this.groupedTaskList.data];
      },
      error: () => {
        // Non-blocking: a failed alert lookup should never break the task list. Leave icons off.
      },
    });
  }

  private buildPatientGroups(records: ClinicalTask[]): PatientTaskGroup[] {
    const groupMap = new Map<string, PatientTaskGroup>();
    const orderedGroups: PatientTaskGroup[] = [];

    for (const task of records) {
      const patientId = task.patient_id;
      if (!patientId) {
        continue;
      }
      let group = groupMap.get(patientId);
      if (!group) {
        const patientObj = task['patient'] ?? null;
        group = {
          patient_id: patientId,
          patientName: task['patientName'] ?? task['patient_name'] ?? '',
          patient: patientObj,
          userTags: this.patientTags(patientObj),
          taskCount: 0,
          hasPinned: false,
          highestPriority: null,
          tasks: [],
        };
        groupMap.set(patientId, group);
        orderedGroups.push(group);
      }
      group.tasks.push(task);
      if (task.pinned) {
        group.hasPinned = true;
      }
    }

    for (const group of orderedGroups) {
      group.taskCount = group.tasks.length;
      group.highestPriority = this.getHighestPriority(group.tasks);
      group.oldestDueDate = this.getOldestDueDate(group.tasks);
      group.hasMissedCall = group.tasks.some((t) => t.type === 'Missed Call');
      group.hasPostHospitalization = group.tasks.some((t) => t.type === 'Post-hospitalization outreach');
      const pinnedTasks = group.tasks.filter((t) => t.pinned);
      const unpinnedTasks = group.tasks.filter((t) => !t.pinned);
      group.tasks = [...pinnedTasks, ...unpinnedTasks];
    }

    const groupsWithPinned = orderedGroups.filter((g) => g.hasPinned);
    const groupsWithoutPinned = orderedGroups.filter((g) => !g.hasPinned);
    return [...groupsWithPinned, ...groupsWithoutPinned];
  }

  private getOldestDueDate(tasks: ClinicalTask[]): Date | null {
    let oldest: Date | null = null;
    for (const task of tasks) {
      const due = task.due_date instanceof Date ? task.due_date : task.due_date ? new Date(task.due_date) : null;
      if (due && (!oldest || due < oldest)) {
        oldest = due;
      }
    }
    return oldest;
  }

  private patientTags(patient: { user_tags?: UserTag[] } | null): string {
    if (!patient?.user_tags?.length) {
      return 'N/A';
    }
    const tagList = patient.user_tags
      .filter((tag) => !!tag?.name)
      .map((tag) =>
        typeof tag.name === 'string' ? tag.name[0].toLocaleUpperCase() + tag.name.substring(1) : 'No Tags'
      );
    return `Tags: ${tagList.reduce((acc, cur) => `${acc}, ${cur}`, '')}`;
  }

  private getHighestPriority(tasks: ClinicalTask[]): ClinicalTaskPriority | null {
    let topRank = 0;
    for (const task of tasks) {
      const rank = task.priority ? TaskHistoryComponent.PRIORITY_RANK[task.priority] : 0;
      if (rank > topRank) {
        topRank = rank;
      }
    }
    return topRank === 0 ? null : TaskHistoryComponent.PRIORITY_BY_RANK[topRank - 1];
  }
}
